import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bounce, toast } from "react-toastify";
import {
    fetchNotificationTemplateList,
    updateNotificationTemplateSendType,
} from "../../../utils/apiUtils";

const NOTIFICATION_TEMPLATE_LIST_KEY = ["notificationTemplateList"];

/**
 * TanStack Query + mutation for the notification template list and optimistic send-type toggles.
 * `updateSendTypeMutation` rolls back on error and shows a toast.
 *
 * @returns {object} All `useQuery` fields for the template-list query, plus:
 *   - `templateList` — `{ template: string, sendType: string }[]` (convenience; `data?.templateList ?? []`).
 *   - `updateSendTypeMutation` — TanStack `useMutation` result for `updateNotificationTemplateSendType` (optimistic update + toast on error).
 */
export function useTemplates() {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: NOTIFICATION_TEMPLATE_LIST_KEY,
        queryFn: fetchNotificationTemplateList,
    });

    const updateSendTypeMutation = useMutation({
        mutationFn: updateNotificationTemplateSendType,
        onMutate: async ({ template, sendType }) => {
            await queryClient.cancelQueries({
                queryKey: NOTIFICATION_TEMPLATE_LIST_KEY,
            });

            const previous = queryClient.getQueryData(
                NOTIFICATION_TEMPLATE_LIST_KEY,
            );

            queryClient.setQueryData(NOTIFICATION_TEMPLATE_LIST_KEY, (old) => {
                if (!old?.templateList) return old;
                return {
                    ...old,
                    templateList: old.templateList.map((row) =>
                        row.template === template
                            ? { ...row, sendType }
                            : row,
                    ),
                };
            });

            return { previous };
        },
        onError: (err, _variables, context) => {
            if (context) {
                queryClient.setQueryData(
                    NOTIFICATION_TEMPLATE_LIST_KEY,
                    context.previous,
                );
            }

            toast.error("Error: Could not update template send type.", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
                transition: Bounce,
            });
        },
    });

    const templateList = query.data?.templateList ?? [];

    return {
        ...query,
        templateList,
        updateSendTypeMutation,
    };
}
